import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, ArrowUpDown, Scale, Loader2, Info, X, Layers, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getTax, logEvent } from "@/lib/api";
import { NOT_ADVICE } from "@/components/Disclaimers";

const COLS = [
  { key: "country", label: "Country", num: false },
  { key: "income_tax_top_rate_pct", label: "Income", num: true, max: 60 },
  { key: "corporate_tax_rate_pct", label: "Corporate", num: true, max: 40 },
  { key: "vat_gst_standard_rate_pct", label: "VAT/GST", num: true, max: 27 },
  { key: "social_security_employee_pct", label: "Social sec.", num: true, max: 25 },
];
const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : `${v}%`);
const hasSpecial = (r) => r.special_individual_tax_schemes && !/not researched/i.test(r.special_individual_tax_schemes);
const isTerritorial = (r) => (r.tax_system || "").toLowerCase().includes("territorial");

const RateCell = ({ v, max }) => (
  <div className="relative">
    {v !== null && v !== undefined && v !== "" && (
      <div className="absolute inset-y-1 left-0 rounded bg-[hsl(var(--atlas-navy))]/10" style={{ width: `${Math.min(100, (Number(v) / max) * 100)}%` }} />
    )}
    <span className="relative tabular-nums text-sm">{fmt(v)}</span>
  </div>
);

export default function TaxExplorer() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [system, setSystem] = useState("all");
  const [onlyZero, setOnlyZero] = useState(false);
  const [onlySpecial, setOnlySpecial] = useState(false);
  const [sort, setSort] = useState({ key: "country", dir: 1 });
  const [density, setDensity] = useState("comfortable");
  const [selected, setSelected] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const highlight = params.get("highlight");
  const [pulse, setPulse] = useState(null);
  const rowRefs = useRef({});

  useEffect(() => { getTax().then((d) => setRows(d.countries)).catch(() => toast.error("Failed to load tax data")).finally(() => setLoading(false)); }, []);

  // Deep-link handler: when arriving with ?highlight=Portugal, scroll to that row
  // and give it a 3-second pulse. Then clean up the URL.
  useEffect(() => {
    if (!highlight || loading || rows.length === 0) return;
    // ensure the target row is not filtered out
    if (q && !highlight.toLowerCase().includes(q.toLowerCase())) setQ("");
    const t = setTimeout(() => {
      const el = rowRefs.current[highlight];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setPulse(highlight);
        setTimeout(() => setPulse(null), 3000);
        logEvent("tax_highlight_arrived", null, { country: highlight });
      }
      // strip the param so a back/forward doesn't re-trigger endlessly
      const next = new URLSearchParams(params);
      next.delete("highlight");
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [highlight, loading, rows.length]);

  const filtered = useMemo(() => {
    let r = rows.filter((x) => x.country.toLowerCase().includes(q.toLowerCase()));
    if (system === "territorial") r = r.filter(isTerritorial);
    if (system === "worldwide") r = r.filter((x) => !isTerritorial(x));
    if (onlyZero) r = r.filter((x) => Number(x.income_tax_top_rate_pct) === 0);
    if (onlySpecial) r = r.filter(hasSpecial);
    const { key, dir } = sort;
    r = [...r].sort((a, b) => {
      const av = a[key], bv = b[key];
      if (key === "country") return dir * String(av).localeCompare(String(bv));
      const an = av === null || av === undefined || av === "" ? -1 : Number(av);
      const bn = bv === null || bv === undefined || bv === "" ? -1 : Number(bv);
      return dir * (an - bn);
    });
    return r;
  }, [rows, q, system, onlyZero, onlySpecial, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));
  const toggleSelect = (c) => setSelected((s) => s.includes(c) ? s.filter((x) => x !== c) : s.length < 4 ? [...s, c] : (toast.info("Compare up to 4 countries"), s));
  const selectedRows = rows.filter((r) => selected.includes(r.country));
  const rowH = density === "compact" ? "h-10" : "h-12";

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[hsl(var(--atlas-navy))]">Global Tax Explorer</h1>
          <p className="mt-2 text-muted-foreground flex items-center gap-1.5">Personal, corporate, VAT &amp; social-security rates across {rows.length} jurisdictions.
            <TooltipProvider><Tooltip><TooltipTrigger><Info className="h-3.5 w-3.5" /></TooltipTrigger><TooltipContent className="max-w-xs">{NOT_ADVICE}</TooltipContent></Tooltip></TooltipProvider>
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-6 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="tax-explorer-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search countries…" className="pl-9 bg-white/80" />
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="tax-explorer-filters">
          <ToggleGroup type="single" value={system} onValueChange={(v) => v && setSystem(v)} className="bg-white/70 rounded-lg border p-0.5">
            <ToggleGroupItem value="all" className="text-xs px-3">All systems</ToggleGroupItem>
            <ToggleGroupItem value="territorial" className="text-xs px-3">Territorial</ToggleGroupItem>
            <ToggleGroupItem value="worldwide" className="text-xs px-3">Worldwide</ToggleGroupItem>
          </ToggleGroup>
          <Button variant={onlyZero ? "default" : "outline"} size="sm" onClick={() => setOnlyZero((v) => !v)} data-testid="tax-filter-zero" className={onlyZero ? "bg-[hsl(var(--atlas-navy))] text-white" : ""}>0% income tax</Button>
          <Button variant={onlySpecial ? "default" : "outline"} size="sm" onClick={() => setOnlySpecial((v) => !v)} data-testid="tax-filter-special" className={onlySpecial ? "bg-[hsl(var(--atlas-navy))] text-white" : ""}>Special regime</Button>
          <ToggleGroup type="single" value={density} onValueChange={(v) => v && setDensity(v)} data-testid="density-toggle" className="bg-white/70 rounded-lg border p-0.5">
            <ToggleGroupItem value="comfortable" className="text-xs px-2"><Layers className="h-3.5 w-3.5" /></ToggleGroupItem>
            <ToggleGroupItem value="compact" className="text-xs px-2">Compact</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {selected.length > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {selected.map((c) => (<Badge key={c} variant="secondary" className="gap-1 pr-1">{c}<button onClick={() => toggleSelect(c)}><X className="h-3 w-3" /></button></Badge>))}
          <Button size="sm" onClick={() => setCompareOpen(true)} data-testid="tax-explorer-compare-button" className="bg-[hsl(var(--atlas-teal))] text-white"><Scale className="h-4 w-4 mr-1" /> Compare ({selected.length})</Button>
        </div>
      )}

      {/* Persistent CTA: bridge from tax comparison into the eligibility funnel */}
      <div
        data-testid="tax-explorer-eligibility-cta"
        className="mt-4 flex items-start sm:items-center gap-3 rounded-xl border border-[hsl(var(--atlas-navy))]/15 bg-gradient-to-r from-[hsl(var(--accent))] to-white px-4 py-3 flex-wrap"
      >
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-semibold text-[hsl(var(--atlas-navy))]">
            Comparing tax rates? See which of these citizenships you already qualify for.
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Free personalized eligibility check across 200+ countries: descent, marriage, naturalization, investment, residence.
          </p>
        </div>
        <Link
          to="/check"
          data-testid="tax-explorer-eligibility-cta-button"
          onClick={() => logEvent("tax_explorer_cta_clicked", null, { source: "banner" })}
          className="inline-flex items-center gap-1 rounded-lg bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white text-sm font-medium px-3 py-2 shrink-0"
        >
          Check your eligibility <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--atlas-navy))]" /></div>
      ) : (
        <Card className="mt-4 bg-white overflow-hidden">
          <div className="overflow-auto thin-scroll max-h-[70vh]">
            <table className="w-full text-left">
              <thead className="sticky top-0 z-10 bg-[hsl(var(--muted))] shadow-[0_1px_0_rgba(15,39,64,0.1)]">
                <tr>
                  <th className="px-3 py-2.5 w-8"></th>
                  {COLS.map((c) => (
                    <th key={c.key} className={`px-3 py-2.5 text-xs font-semibold text-[hsl(var(--atlas-slate))] ${c.num ? "text-right" : ""}`}>
                      <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-[hsl(var(--atlas-navy))]">
                        {c.label} <ArrowUpDown className="h-3 w-3 opacity-50" />
                      </button>
                    </th>
                  ))}
                  <th className="px-3 py-2.5 text-xs font-semibold text-[hsl(var(--atlas-slate))]">System</th>
                  <th className="px-3 py-2.5 text-xs font-semibold text-[hsl(var(--atlas-slate))]">Special regime</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isPulsing = pulse === r.country;
                  const linkTarget = `/check?target=${encodeURIComponent(r.country)}`;
                  const canDeepLink = r.has_citizenship_data;
                  return (
                  <tr
                    key={r.country}
                    ref={(el) => { if (el) rowRefs.current[r.country] = el; }}
                    className={`border-t hover:bg-[hsl(var(--accent))]/40 ${rowH} ${
                      isPulsing ? "bg-[hsl(var(--atlas-teal))]/15 ring-2 ring-[hsl(var(--atlas-teal))] ring-inset" : ""
                    } transition-colors`}
                    data-testid="tax-row"
                  >
                    <td className="px-3"><Checkbox checked={selected.includes(r.country)} onCheckedChange={() => toggleSelect(r.country)} data-testid={`tax-select-${r.country}`} /></td>
                    <td className="px-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {canDeepLink ? (
                          <Link
                            to={linkTarget}
                            data-testid={`tax-row-eligibility-link-${r.country}`}
                            onClick={() => logEvent("tax_explorer_row_clicked", null, { country: r.country })}
                            className="group inline-flex items-center gap-1 font-medium text-[hsl(var(--atlas-navy))] hover:text-[hsl(var(--atlas-teal))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] rounded-sm"
                            title={`See how you'd qualify for ${r.country} citizenship`}
                          >
                            <span className="underline decoration-transparent group-hover:decoration-[hsl(var(--atlas-teal))]/40 underline-offset-4">
                              {r.country}
                            </span>
                            <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </Link>
                        ) : (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  data-testid={`tax-row-nolink-${r.country}`}
                                  className="inline-flex items-center gap-1 font-medium text-[hsl(var(--atlas-navy))] cursor-default"
                                >
                                  {r.country}
                                  <Info className="h-3 w-3 text-muted-foreground/60" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs text-xs">
                                Citizenship pathway data for {r.country} is pending. This row cannot yet link into an eligibility check.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {r.tax_data_confidence && r.tax_data_confidence !== "high" && (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  data-testid={`tax-confidence-${r.country}`}
                                  className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${
                                    r.tax_data_confidence === "low"
                                      ? "border-rose-300 bg-rose-50 text-rose-800"
                                      : "border-amber-300 bg-amber-50 text-amber-900"
                                  }`}
                                >
                                  <AlertTriangle className="h-2.5 w-2.5" />
                                  {r.tax_data_confidence}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs text-xs">
                                Figures for {r.country} carry <span className="font-medium">{r.tax_data_confidence}</span> confidence
                                {r.tax_last_verified ? ` (last verified ${r.tax_last_verified})` : ""}. Spot-check before relying on them.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </td>
                    <td className="px-3 text-right"><RateCell v={r.income_tax_top_rate_pct} max={60} /></td>
                    <td className="px-3 text-right"><RateCell v={r.corporate_tax_rate_pct} max={40} /></td>
                    <td className="px-3 text-right"><RateCell v={r.vat_gst_standard_rate_pct} max={27} /></td>
                    <td className="px-3 text-right"><RateCell v={r.social_security_employee_pct} max={25} /></td>
                    <td className="px-3">{isTerritorial(r) ? <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-[11px]">Territorial</Badge> : <Badge variant="secondary" className="text-[11px]">Worldwide</Badge>}</td>
                    <td className="px-3">{hasSpecial(r) ? <Badge variant="outline" className="text-[11px] border-[hsl(var(--atlas-teal))]/40 text-[hsl(var(--atlas-teal))]">Yes</Badge> : <span className="text-xs text-muted-foreground">—</span>}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="py-12 text-center text-muted-foreground">No countries match your filters.</div>}
          </div>
          <div className="px-4 py-2 text-xs text-muted-foreground border-t flex items-center justify-between gap-3 flex-wrap">
            <span>{filtered.length} of {rows.length} jurisdictions · rates 2026 vintage</span>
            {(() => {
              const mods = rows.filter((r) => r.tax_data_confidence === "moderate").length;
              const lows = rows.filter((r) => r.tax_data_confidence === "low").length;
              if (mods + lows === 0) return null;
              return (
                <span className="flex items-center gap-2" data-testid="tax-confidence-legend">
                  <AlertTriangle className="h-3 w-3 text-amber-700" />
                  <span>{mods} moderate · {lows} low confidence. Spot-check before relying.</span>
                </span>
              );
            })()}
          </div>
        </Card>
      )}

      {/* Compare dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-5xl" data-testid="tax-explorer-compare-drawer">
          <DialogHeader><DialogTitle className="font-display text-[hsl(var(--atlas-navy))]">Compare jurisdictions</DialogTitle></DialogHeader>
          <div className="grid gap-4 overflow-auto" style={{ gridTemplateColumns: `repeat(${Math.max(1, selectedRows.length)}, minmax(180px, 1fr))` }}>
            {selectedRows.map((r) => (
              <div key={r.country} className="rounded-xl border p-4">
                <div className="font-display font-semibold text-[hsl(var(--atlas-navy))]">{r.country}</div>
                <div className="mt-2 space-y-1.5 text-sm">
                  {[["Income", fmt(r.income_tax_top_rate_pct)], ["Corporate", fmt(r.corporate_tax_rate_pct)], ["VAT/GST", fmt(r.vat_gst_standard_rate_pct)], ["Social sec.", fmt(r.social_security_employee_pct)], ["Wealth", r.wealth_tax || "—"], ["Inheritance", r.inheritance_estate_tax || "—"], ["System", r.tax_system || "—"]].map(([l, v]) => (
                    <div key={l} className="flex justify-between gap-2"><span className="text-muted-foreground">{l}</span><span className="font-medium text-right tabular-nums">{v}</span></div>
                  ))}
                </div>
                {hasSpecial(r) && <p className="mt-2 text-xs text-muted-foreground">{r.special_individual_tax_schemes}</p>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
